import { fetchDeltaSyncData } from './api';
import { DELTA_SYNC_API_VER, DELTA_SYNC_CLIENT_VER } from './constants';
import { getLastSyncDate, setLastSyncDate } from './last-sync-date.atom';
import { getCurrentWorkspaceId } from '../../state/current-workspace-id.atom';
import { NEW_FEATURES, setFeatures } from '../../state/feature-toggle/new-features';
import { forceReloadProfilesQuery } from '../../state/profiles-table/profiles-query';
import { purgeCacheDB } from '../idb-cache/cache-db';
import { getCacheDBName } from '../idb-cache/cache-db-name.atom';
import { resetObjectPool } from '../object-pool.atom';
import { handleAcceptedTransactions } from '../transactions/handle-accepted-transaction';
import type {
  DeltaSyncEvent,
  ObjectPoolEntity,
  ObjectPoolFolderProfile,
  ObjectPoolProfile,
  ObjectPoolTransaction,
} from '../types';
import { ObjectPoolShare } from '../types/object-pool-share';
import { logToDbgMonitor } from '../../dbg/monitor';

const MIN_DATE = new Date(0);
const MAX_REQUESTS = 100;

type PartialDeltaSyncResStatus = 'sync-done' | 'sync-disabled' | 'continue';

export const performGlobalDeltaSync = async (): Promise<void> => {
  if (!NEW_FEATURES.objectPool) {
    return;
  }

  let retriesLeft = MAX_REQUESTS;
  while (retriesLeft) {
    retriesLeft--;
    const deltaSyncRes = await performPartialDeltaSync();
    if (!deltaSyncRes || deltaSyncRes.resStatus !== 'continue') {
      return;
    }
  }
};

const handleDisableObjectPoolEvent = (_event: DeltaSyncEvent): void => {
  setFeatures({ objectPool: false });
  resetObjectPool();
  const cacheDbName = getCacheDBName();
  if (cacheDbName) {
    purgeCacheDB(cacheDbName);
  }

  forceReloadProfilesQuery();
};

const performPartialDeltaSync = async (): Promise<{ resStatus: PartialDeltaSyncResStatus } | null> => {
  logToDbgMonitor('delta-sync', { fn: 'performPartialDeltaSync' });
  const lastSyncDate = getLastSyncDate();
  const syncData = await fetchDeltaSyncData({
    workspaceId: getCurrentWorkspaceId(),
    syncSince: (lastSyncDate || MIN_DATE).toISOString(),
    apiVer: DELTA_SYNC_API_VER,
    clientVer: DELTA_SYNC_CLIENT_VER,
  });

  const {
    workspaces = [],
    shares = [],
    events = [],
    latestSyncDate = null,
    isSyncDone = false,
  } = syncData || {};

  const disableObjectPoolEvent = events.find((event) => event.event === 'disable-object-pool');
  if (disableObjectPoolEvent) {
    handleDisableObjectPoolEvent(disableObjectPoolEvent);

    return { resStatus: 'sync-disabled' };
  }

  const [workspace] = workspaces;
  if (!workspace) {
    console.warn('No workspace in delta-sync');

    return null;
  }

  if (!latestSyncDate) {
    console.warn('No latest sync date in delta-sync');

    return null;
  }

  const { profiles, foldersProfiles } = workspace;

  const profileObjectPoolEntities: ObjectPoolProfile[] = profiles.map((profile) => ({ ...profile, objectPoolEntityType: 'profile' }));
  const folderProfileObjectPoolEntities: ObjectPoolFolderProfile[] = foldersProfiles.map((fp) => ({ ...fp, objectPoolEntityType: 'folder-profile' }));
  const shareObjectPoolEntities: ObjectPoolShare[] = shares.map((sh) => ({ ...sh, objectPoolEntityType: 'share' }));

  // TODO: create single transaction object with an array
  const objectPoolTransactions: ObjectPoolTransaction<ObjectPoolEntity>[] = [
    ...profileObjectPoolEntities,
    ...folderProfileObjectPoolEntities,
    ...shareObjectPoolEntities,
  ].map((entity) => ({
    entity,
    entityType: entity.objectPoolEntityType,
    transactionType: entity.deleted ? 'D' : 'P',
  }));

  handleAcceptedTransactions(objectPoolTransactions);
  setLastSyncDate(new Date(latestSyncDate));

  return { resStatus: isSyncDone ? 'sync-done' : 'continue' };
};
